import React from "react";
import { Link } from "react-router-dom";
import RelatedContentRail from "../components/RelatedContentRail";

const READINESS_DIMENSIONS = [
  {
    title: "Business Prioritization",
    scoreLabel: "High Impact",
    checks: [
      "Top 3 AI use cases mapped to clear business KPIs",
      "Executive owner assigned per initiative",
      "Expected value defined in 90-day horizon",
    ],
  },
  {
    title: "Data & Systems",
    scoreLabel: "Critical",
    checks: [
      "Core data sources cataloged and quality-reviewed",
      "Access controls and retention policies documented",
      "Integration feasibility validated for top use cases",
    ],
  },
  {
    title: "People & Process",
    scoreLabel: "Execution",
    checks: [
      "Cross-functional squad identified",
      "Decision rights and escalation model documented",
      "Pilot cadence (weekly) and feedback loop in place",
    ],
  },
  {
    title: "Risk & Governance",
    scoreLabel: "Trust",
    checks: [
      "PII handling approach approved by legal/security",
      "Model behavior monitoring and incident path defined",
      "Policy for human review and override established",
    ],
  },
];

const MATURITY_LEVELS = [
  {
    level: "Level 1 - Curious",
    desc: "Exploring opportunities but no repeatable AI delivery process yet.",
  },
  {
    level: "Level 2 - Structured",
    desc: "Pilot framework exists with ownership, prioritization, and measurable outcomes.",
  },
  {
    level: "Level 3 - Scaled",
    desc: "AI initiatives run as a portfolio with governance, observability, and iteration loops.",
  },
];

const RELATED_CONTENT = [
  { to: "/insights", title: "Insights Hub", desc: "Review trends and strategic guidance." },
  { to: "/playbooks", title: "Playbooks", desc: "Use deployment-ready implementation patterns." },
  { to: "/ai", title: "AI Services", desc: "Move from planning into production delivery." },
  { to: "/templates", title: "Templates", desc: "Accelerate pilots with structured assets." },
];

export default function AIReadiness() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-slate-900 via-purple-950 to-indigo-950 text-white px-4 py-20">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.24em] text-purple-300 font-bold">Assessment</p>
          <h1 className="text-4xl sm:text-5xl font-black mt-3 leading-tight">AI Readiness Guide for Modern Teams</h1>
          <p className="text-lg text-slate-300 max-w-3xl mt-5">
            Before scaling AI, validate readiness across business goals, data quality, operating model, and governance. This guide helps teams avoid expensive false starts.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/playbooks" className="px-5 py-2.5 rounded-lg bg-white text-indigo-700 font-bold hover:bg-indigo-50 transition">View Playbooks</Link>
            <Link to="/consulting" className="px-5 py-2.5 rounded-lg border border-white/40 text-white font-semibold hover:bg-white/10 transition">Book Assessment Call</Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-black text-slate-900 mb-8">Readiness Dimensions</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {READINESS_DIMENSIONS.map((d) => (
            <div key={d.title} className="border border-gray-200 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-black text-lg text-slate-900">{d.title}</h3>
                <span className="text-[10px] uppercase tracking-wider font-bold bg-indigo-100 text-indigo-700 rounded-full px-2 py-1">{d.scoreLabel}</span>
              </div>
              <ul className="space-y-2">
                {d.checks.map((c) => (
                  <li key={c} className="text-sm text-slate-600 flex items-start gap-2">
                    <span className="text-indigo-600 font-bold">•</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-black text-slate-900 mb-8">Maturity Levels</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {MATURITY_LEVELS.map((m) => (
              <div key={m.level} className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="font-bold text-slate-900 mb-2">{m.level}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16 grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-700 text-white p-8 sm:p-10">
          <h2 className="text-3xl font-black mb-3">Next Step: Pilot to Production</h2>
          <p className="text-indigo-100 mb-6 max-w-2xl">
            If your team is at Level 1 or 2, start with one high-confidence pilot and a measurable business KPI. We can help design and deliver that pilot in 4-6 weeks.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/ai" className="px-5 py-2.5 rounded-lg bg-white text-indigo-700 font-bold hover:bg-indigo-50 transition">Explore AI Services</Link>
            <Link to="/templates" className="px-5 py-2.5 rounded-lg border border-white/40 text-white font-semibold hover:bg-white/10 transition">Use Templates</Link>
          </div>
        </div>
        <RelatedContentRail section="ai_readiness" links={RELATED_CONTENT} />
      </section>
    </main>
  );
}
